import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/Button';

const forgotSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type ForgotFormData = z.infer<typeof forgotSchema>;

const inputClasses =
  'w-full bg-slate-50 border border-slate-200 rounded-lg p-3 text-slate-700 placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none';

const labelClasses = 'block text-sm font-bold text-slate-700 mb-1.5';

const errorClasses = 'text-xs text-error mt-1';

export default function ForgotPassword() {
  const { requestPasswordReset } = useAuth();
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotFormData>({
    resolver: zodResolver(forgotSchema),
  });

  const onSubmit = async (data: ForgotFormData) => {
    setServerError(null);
    try {
      await requestPasswordReset({ email: data.email });
      setSubmitted(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setServerError(message);
    }
  };

  return (
    <div className="bg-background">
      {/* ===== SECTION 1: PAGE HERO ===== */}
      <section className="relative py-24 lg:py-32 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-15" style={{ backgroundImage: "url('/images/goldson-wade-2018-hero.jpg')" }} />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800/90 to-primary-dark/85" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 border border-primary/30 mb-6">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            <span className="text-xs font-bold tracking-widest uppercase text-white">Account Recovery</span>
          </span>
          <h1 className="heading-hero text-white mb-6 max-w-4xl mx-auto">Forgot Your Password?</h1>
          <p className="text-lg text-slate-200 leading-relaxed max-w-2xl mx-auto">
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>
      </section>

      {/* ===== SECTION 2: FORM ===== */}
      <section className="py-24 bg-white">
        <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
          {submitted ? (
            <div className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-slate-900">Check Your Email</h2>
              <p className="text-slate-600 text-sm leading-relaxed">
                We sent a password reset link to your email address.
                If you don't see it within a few minutes, check your spam folder.
              </p>
              <Link
                to="/login"
                className="inline-block mt-4 text-primary font-bold hover:underline text-sm"
              >
                Back to Login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
              {serverError && (
                <div className="rounded-lg bg-error/10 border border-error/20 p-4 text-sm text-error">
                  {serverError}
                </div>
              )}

              <div>
                <label htmlFor="forgot-email" className={labelClasses}>Email</label>
                <input
                  id="forgot-email"
                  type="email"
                  placeholder="you@example.com"
                  className={inputClasses}
                  {...register('email')}
                />
                {errors.email && <p className={errorClasses}>{errors.email.message}</p>}
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Reset Link'}
              </Button>

              <p className="text-center text-sm text-slate-600">
                Remember your password?{' '}
                <Link to="/login" className="text-primary font-bold hover:underline">
                  Log in
                </Link>
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
